import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaBoxes, FaStore, FaMoneyBillWave } from "react-icons/fa";
import { deleteProduct, getAllProducts } from "../api/apiServices";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    (async () => {
      try {
        const res = await getAllProducts();
        setProducts(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async (e, productId) => {
  e.stopPropagation();

  const confirmed = window.confirm(
    "Are you sure you want to delete this product?"
  );

  if (!confirmed) return;

  try {
    // Optimistic UI update
    setProducts((prev) =>
      prev.filter((p) => p._id !== productId)
    );

    await deleteProduct(productId);
  } catch (err) {
    console.error(err);
    alert("Failed to delete product. Please try again.");
  }
};


  /* =======================
      FILTER + PAGINATION
  ======================== */
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.storeName?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const paginatedProducts = filteredProducts.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  /* =======================
      SUMMARY METRICS
  ======================== */
  const totalProducts = products.length;

  const totalStock = products.reduce(
    (sum, p) => sum + (p.stockQuantity || 0),
    0
  );

  const totalValue = products.reduce(
    (sum, p) => sum + p.pricePerUnit * p.stockQuantity,
    0
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* ================= HERO ================= */}
      <div className="relative h-[350px] bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
            All Products
          </h1>
          <p className="opacity-90">
            Manage and monitor all listed products
          </p>
        </div>
      </div>

      {/* ================= SUMMARY ================= */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto px-4 mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <SummaryCard
          title="Total Products"
          value={totalProducts}
          icon={<FaBoxes size={34} />}
          color="from-blue-500 to-indigo-600"
        />

        <SummaryCard
          title="Total Stock"
          value={totalStock}
          icon={<FaStore size={34} />}
          color="from-green-500 to-emerald-600"
        />

        <SummaryCard
          title="Total Inventory Value"
          value={`₦${totalValue.toLocaleString()}`}
          icon={<FaMoneyBillWave size={34} />}
          color="from-purple-500 to-pink-600"
        />
      </motion.div>

      {/* ================= PRODUCTS LIST ================= */}
      <div className="max-w-6xl mx-auto px-4 mt-16 mb-20">
        <h2 className="text-2xl font-bold mb-6">Products</h2>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by product name or store..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-1/2 px-4 py-2 mb-6 border rounded-lg focus:ring-2 focus:ring-indigo-500"
        />

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProducts.map((product) => (
            <div
              key={product._id}
              onClick={() => setSelectedProduct(product)}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition cursor-pointer"
            >
              <img
                src={product.images?.[0]}
                alt={product.name}
                className="h-44 w-full object-cover rounded-t-xl"
              />

              <div className="p-4 relative">
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDelete(e, product._id)}
                  className="absolute top-3 right-3 text-red-500 hover:text-red-700 text-sm font-semibold">
                    Delete
                </button>
                <p className="font-bold text-gray-800">{product.name}</p>
                <p className="text-sm text-gray-500">
                  {product.storeName}
                </p>

                <div className="flex justify-between items-center mt-3">
                  <span className="font-bold text-[#f97316]">
                    ₦{product.pricePerUnit.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400">
                    Stock: {product.stockQuantity}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className={`px-4 py-2 rounded-lg font-semibold ${
              page === 1
                ? "bg-gray-200 text-gray-400"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            Previous
          </button>

          <span className="font-medium text-gray-600">
            Page {page} of {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            className={`px-4 py-2 rounded-lg font-semibold ${
              page === totalPages
                ? "bg-gray-200 text-gray-400"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

/* =======================
    REUSABLE COMPONENTS
======================= */

function SummaryCard({ title, value, icon, color }) {
  return (
    <div
      className={`p-6 bg-gradient-to-r ${color} text-white rounded-2xl shadow-lg`}
    >
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm opacity-80">{title}</p>
          <h3 className="text-3xl font-bold">{value}</h3>
        </div>
        {icon}
      </div>
    </div>
  );
}

function ProductModal({ product, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        <h3 className="text-xl font-bold mb-4">{product.name}</h3>

        <p className="text-sm text-gray-600 mb-2">
          {product.description}
        </p>

        <div className="text-sm space-y-1">
          <p><b>Store:</b> {product.storeName}</p>
          <p><b>Category:</b> {product.category?.name}</p>
          <p><b>Location:</b> {product.city}, {product.state}</p>
          <p><b>Seller:</b> {product.sellerName}</p>
          <p><b>Stock:</b> {product.stockQuantity}</p>
        </div>

        <div className="mt-4 flex justify-between font-bold">
          <span>Price</span>
          <span>₦{product.pricePerUnit.toLocaleString()}</span>
        </div>
        <button
        onClick={() => onDelete(product._id)}
        className="mt-4 w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700">
          Delete Product
        </button>
      </div>
    </div>
  );
}
